<template>
  <div :class="rootClass">
    <div :class="[`${rootClass}__container`, 'container']">
      <aside :class="`${rootClass}__aside`">
        <VehicleCard
          :model="vehicle_name"
          :image="picture"
          :price="price"
          :specs="[]"
        >
          <template #footer>
            <VehicleCardSection :title="lang.included">
              <ul :class="`${rootClass}__list`">
                <li
                    v-for="(extra, index) in includedExtras"
                    :key="`vehicle-card-section-extra-${index}`"
                >
                    <span><i aria-hidden="true" class="fa fa-check"></i></span>&nbsp;
                    <span>{{extra.name}}</span>
                </li>
              </ul>
            </VehicleCardSection>
            <VehicleCardSection :title="lang.extras">
              <div :class="`${rootClass}__extras-list`">
                <template v-for="(extra, index) in paidExtras">
                  <!-- Los que van por persona se piden por cantidad; el resto, se lleva o no se
                       lleva. Lo decide max_units del propio extra, igual que en el contrato. -->
                  <div
                    v-if="extra.max_units > 1"
                    :key="`vehicle-card-section-units-extra-${index}`"
                    :class="`${rootClass}__extra-units`"
                  >
                    <span :class="`${rootClass}__extra-label`">
                      <span :class="`${rootClass}__extra-name`">{{ extra.name }}</span>
                      <span :class="`${rootClass}__extra-price`">{{ extraPriceLabel(extra) }}</span>
                    </span>
                    <!-- Botones de radio y no un <select>: la elección se ve entera sin abrir nada,
                         se elige con un toque y sin estilos sigue siendo un grupo de radios que
                         funciona. El estado visual lo lleva el propio input con :checked. -->
                    <span
                      :class="`${rootClass}__extra-unit-group`"
                      role="group"
                      :aria-label="extra.name"
                    >
                      <label
                        v-for="units in extraUnitOptions(extra)"
                        :key="units"
                        :class="`${rootClass}__extra-unit`"
                      >
                        <input
                          type="radio"
                          :name="'extra' + extra.id"
                          :value="units"
                          :checked="(extras[extra.id] || 0) === units"
                          @change="onExtraUnitsChanged(extra.id, units)"
                        >
                        <span>{{ units }}</span>
                      </label>
                    </span>
                  </div>
                  <CheckboxField
                    v-else
                    :key="`vehicle-card-section-paid-extra-${index}`"
                    :id="'extra' + extra.id"
                    :name="'extra' + extra.id"
                    :value="extra.id"
                    :checked="Boolean(extras[extra.id])"
                    type="switch"
                    @input="onExtraChanged"
                  >
                    <template #label>
                      <div :class="`${rootClass}__extra-label`">
                        <span :class="`${rootClass}__extra-name`">{{ extra.name }}</span>
                        <span :class="`${rootClass}__extra-price`">{{ extraPriceLabel(extra) }}</span>
                      </div>
                    </template>
                  </CheckboxField>
                </template>
              </div>
            </VehicleCardSection>
          </template>
        </VehicleCard>
      </aside>
      <div :class="`${rootClass}__main`">
        <div v-if="isAfterHours" class="alert alert-warning">
          {{ lang.after_hours_message }}
        </div>
        <div v-if="rateError" class="alert alert-warning">
          {{ lang.rates_error || 'We could not calculate the price. Please check your dates and try again.' }}
        </div>
        <div v-if="validationErrors.length > 0" class="alert alert-danger">
          <ul class="mb-0 mt-2">
            <li v-for="(error, index) in validationErrors" :key="index">{{ error }}</li>
          </ul>
        </div>
        <div class="form-card">
          <form :class="`${rootClass}__form`">
            <input type="hidden" v-model="vehicle_id">
            <template v-if="reservedDates">
              <div :class="[`${rootClass}__form-date-time-fields`, `${rootClass}__field`]">
                <datepicker
                  v-model="rent_start"
                  wrapper-class="form-control pull-right"
                  input-class="datepicker-input"
                  style="z-index: 4"
                  :monday-first="mondayFirst"
                  :placeholder=lang.rent_start
                  :disabled-dates="datepicker1.disabled"
                  :highlighted="datepicker1.highlighted"
                  :language= getLocale()
                  @input="onDatepicker1Set"
                />
                <time-selector
                  class="select-form"
                  color="black"
                  :hour="rent_start_hour"
                  :from="regularopeningtime"
                  :to="regularclosingtime"
                  :disabledfrom="timepicker1.disabledFrom"
                  :disabledto="timepicker1.to"
                  :extendedto="extendedto"
                  @change="onTimePicker1Set"
                />
              </div>

              <div :class="[`${rootClass}__form-date-time-fields`, `${rootClass}__field`]">
                <datepicker
                  v-model="rent_end"
                  wrapper-class="form-control pull-right"
                  input-class="datepicker-input"
                  style="z-index: 3"
                  :monday-first="mondayFirst"
                  :placeholder=lang.rent_end
                  :disabled-dates="datepicker2.disabled"
                  :highlighted="datepicker2.highlighted"
                  :open-date="datepicker2.opendate"
                  :disabled="datepicker2.isDisabled"
                  :language= getLocale()
                  @input="onDatepicker2Set"
                  ref="datePicker2"
                />
                <time-selector
                  class="select-form"
                  color="black"
                  :hour="rent_end_hour"
                  :from="regularopeningtime"
                  :to="regularclosingtime"
                  :disabledfrom="timepicker2.disabledFrom"
                  :disabledto="timepicker2.to"
                  :extendedto="extendedto"
                  @change="onTimePicker2Set"
                />
              </div>
            </template>
            <template v-else>
                <loader :loading="loading.show" :color="loading.color"></loader>
            </template>

            <TextField
                id="renter_name"
                name="renter_name"
                :value="renter_name"
                :placeholder="lang.name"
                :class="`${rootClass}__field`"
                @input="updateField"
            />

            <TextField
                id="renter_email"
                name="renter_email"
                :value="renter_email"
                :placeholder="lang.email"
                type="email"
                :class="`${rootClass}__field`"
                @input="updateField"
            />

            <TextField
                id="renter_phone"
                name="renter_phone"
                :value="renter_phone"
                :placeholder="lang.phone"
                type="email"
                :class="`${rootClass}__field`"
                @input="updateField"
            />

            <SelectField
                id="pickup_location"
                name="pickup_location"
                :value="pickup_location"
                :placeholder="lang.pickup_location"
                :options="pickupLocationOptions"
                :class="`${rootClass}__field`"
                :text="pickupText"
                @input="updateField"
            />
              <SelectField
                  v-show="dropoffEnabled"
                  id="dropoff_location"
                  name="dropoff_location"
                  :value="dropoff_location"
                  :placeholder="lang.dropoff_location"
                  :options="dropoffLocationOptions"
                  :class="`${rootClass}__field`"
                  @input="updateField"
              />
            <SelectField
                v-show="isBike"
                id="size"
                name="size"
                :value="size"
                :placeholder="lang.select_size"
                :options="sizesOptions"
                :class="`${rootClass}__field`"
                @input="updateField"
            />

              <VoucherField
                  :value="voucher"
                  :placeholder="lang.voucher_code"
                  :class="`${rootClass}__field`"
                  :status="voucher_status"
                  :error-text="lang.voucher_error_text"
                  :ok-text="lang.voucher_ok_text"
                  @input="applyVoucher"
              />

            <CheckboxField
                id="tos"
                name="tos"
                :class="`${rootClass}__field`"
                :value="tos"
                :checked="tos"
                @input="updateField({ name: 'tos', value: $event.selected })"
            >
                <template #label>
                    <a :href="'/' + bike.locale + '/page/conditions'">{{ lang.tos }}</a>
                </template>
            </CheckboxField>

            <footer :class="`${rootClass}__form-footer`">
              <ul :class="`${rootClass}__summary`">
                <li :class="`${rootClass}__summary-item`">{{ lang.days }}: <strong>{{calculatedRates.days}}</strong></li>
                <li :class="`${rootClass}__summary-item`">{{ lang.vehicle_rent_price }}: <strong v-if="calculatedRates.list_vehicle_rent_price">{{(parseFloat(calculatedRates.list_vehicle_rent_price)).toFixed(2)}} €</strong></li>
                <li v-if="calculatedRates.discount > 0 " :class="`${rootClass}__summary-item`">{{ lang.discount }}: <strong>{{calculatedRates.discount}} €</strong></li>
                <li v-if="calculatedRates.extras_price > 0" :class="`${rootClass}__summary-item`">{{ lang.extras }}: <strong>{{calculatedRates.extras_price}} €</strong></li>
                <li :class="`${rootClass}__summary-item--separator`"></li>
                <li :class="[`${rootClass}__summary-item`, `${rootClass}__summary-item--highlighted`]">{{ lang.subtotal }}: <strong v-if="calculatedRates.subtotal">{{calculatedRates.subtotal}} €</strong></li>
                <li :class="`${rootClass}__summary-item--separator`"></li>
                <li
                    v-for="(taxes,rate) in calculatedRates.taxes"
                    :key="`summary-taxes-${rate}`"
                    :class="`${rootClass}__summary-item`"
                >{{ lang.taxes.replace(':taxes', Math.round(rate * 1000)/10) }}: <strong>{{taxes}}€</strong></li>
                <li :class="`${rootClass}__summary-item--separator`"></li>
                <li v-if="calculatedRates.giftcard_balance > 0" :class="`${rootClass}__summary-item`">{{ lang.giftcard }} {{calculatedRates.voucher_code}} : <strong>-{{calculatedRates.giftcard_balance}} €</strong></li>
                <li :class="[`${rootClass}__summary-item`, `${rootClass}__summary-item--highlighted`]">{{ lang.total }}: <strong v-if="calculatedRates.total">{{calculatedRates.total}}€</strong></li>
              </ul>
              <div :class="`${rootClass}__form-footer-actions`">
                <div  :class="`${rootClass}__conditions`">
                    <details v-if="privacyRows.length" :class="`${rootClass}__privacy`">
                        <summary :class="`${rootClass}__privacy-title`">{{ lang.privacy_basic.title }}</summary>
                        <table :class="`${rootClass}__privacy-table`">
                            <tr v-for="row in privacyRows" :key="`privacy-row-${row.label}`">
                                <th scope="row">{{ row.label }}</th>
                                <td>{{ row.value }}</td>
                            </tr>
                            <tr>
                                <th scope="row">{{ lang.privacy_basic.more_label }}</th>
                                <td>{{ lang.privacy_basic.more }} <a :href="'/' + bike.locale + '/page/privacy'">{{ lang.privacy }}</a></td>
                            </tr>
                        </table>
                    </details>
                    <p v-else>{{ lang.conditions }} <a :href="'/' + bike.locale + '/page/privacy'">{{ lang.privacy }}</a></p>
                </div>
                <button type="button" :class="[`${rootClass}__form-action`, 'btn btn-light btn-lg']" :disabled="disabled" @click="submit">{{ lang.submit }}</button>
              </div>
            </footer>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VehicleCard from './vehicle-card/VehicleCard.vue'
import VehicleCardSection from './vehicle-card/VehicleCardSection.vue'
import TextField from './TextField.vue'
import VoucherField from './VoucherField.vue'
import SelectField from './SelectField.vue'
import CheckboxField from './CheckboxField.vue'
    import Datepicker from 'vuejs-datepicker'
    import TimeSelector from './TimeSelector'
    import Loader from 'vue-spinner/src/PulseLoader.vue'
    import {en, es, de} from 'vuejs-datepicker/dist/locale'
import moment from 'moment-timezone'
import { getDateOf as svcGetDateOf, getDatepickerLocale } from '../services/time-service';
import { getEndOfDay, getEarliestHour as svcGetEarliestHour, applyPastTimeBuffer, isTooLateForToday, getTimeConstraintsForDate, getSameDayEndHourMin, isDayBlocked as svcIsDayBlocked, isHoliday as svcIsHoliday } from '../services/schedule-service';
import { getEarliestAllowedEndDate, getFirstBlockerAfterStart } from '../services/datepicker-service';
    export default {
        components: {
            VehicleCard,
            VehicleCardSection,
            TextField,
            VoucherField,
            SelectField,
            CheckboxField,
            Datepicker,
            Loader,
            TimeSelector
        },
        props: {
            vehicle: {
                required: true,
                type: Object
            },
            priceperday: {
                required: true,
                type: String
            },
            lang: {
                required: true,
                type: Object
            },
            schedule_sunday_end: {
                required: true,
                type: Number
            },
            locations: {
                required: true,
                type: Object
            },
            dropoff: {
                required: true,
                type: Boolean,
                default: false
            },
            blockpastreservation: {
                required: true,
                type: Boolean,
                default: false
            },
            voucher:{
                required: false,
                type: String,
            },
            minreservationdays: {
                required: true,
                type: Number
            },
            preventnextmorningreservations: {
                required: true,
                type: Boolean,
                default: false
            },
            regularopeningtime: {
                required: false,
                type: Number,
                default: 9
            },
            regularclosingtime: {
                required: false,
                type: Number,
                default: 18
            },
            extendedto: {
                required: false,
                type: Number,
                default: false
            },
            tenant: {
                required: true,
                type: String,
                default: '',
            },
            airport_location_id: {
                required: true,
                type: [String, Number]
            },
            serverTime: {
                required: true,
                type: String
            },
            timezone: {
                required: true,
                type: String
            },
            oldInput: {
                required: false,
                type: Object,
                default: () => ({})
            },
        },
        data() {
            return {
                rootClass: 'checkout-page',
                vehicle_name: this.vehicle['name'],
                price: this.priceperday,
                picture: this.vehicle['attachments'][0]['url'],
                vehicle_id: this.vehicle['id'],
                vehicle_type: this.vehicle['type'],
                pickup_options: this.vehicle['pickup_options'],
                extras_list: Object.keys(this.vehicle['extras']).map((key) => ({ ...this.vehicle['extras'][key], name: key })),
                // Lo elegido, con sus unidades: { id: unidades }. Era una lista de ids con la unidad
                // dada por supuesta, y las unidades son justamente lo que el cliente puede elegir.
                extras: {},
                bike: bike,
                reservedDates: null,
                holidays: null,
                renter_name: this.oldInput.renter_name || '',
                renter_email: this.oldInput.renter_email || '',
                renter_phone: this.oldInput.renter_phone || '',
                rent_start: this.oldInput.rent_start || '',
                rent_start_hour: this.oldInput.rent_start_hour || this.regularopeningtime,
                rent_end: this.oldInput.rent_end || '',
                rent_end_hour: this.oldInput.rent_end_hour || this.regularopeningtime,
                size: this.oldInput.vehicle_size || '',
                pickupText: '',
                voucher_status:'none',
                pickup_location: this.oldInput.pickup_location || '',
                dropoff_location: this.oldInput.dropoff_location || '',
                tos: false,
                mondayFirst:true,
                calculatedRates: {},
                // Para que combinacion de datos son las cifras que hay pintadas. El boton
                // espera a que cuadre con lo que el formulario pide ahora mismo: enviar
                // antes es reservar a un precio que el cliente no ha llegado a ver.
                calculatedRatesFor: null,
                rateError: false,
                loading:{
                    show: true,
                    color: '#194482',
                },
                datepicker1: {
                    disabled:{
                        to: null,
                        customPredictor: this.isDayDisabled
                    },
                    highlighted: {
                        from:'',
                        to: '',
                    }
                },
                datepicker2: {
                    isDisabled: true,
                    disabled:{
                        from: '',
                        to: null,
                        customPredictor: this.isDayDisabled,
                    },
                    highlighted: {
                        from:'',
                        to: '',
                    },
                    opendate: null
                },
                timepicker1:{
                    disabledFrom: this.regularopeningtime,
                    to: this.extendedto || this.regularclosingtime
                },
                timepicker2:{
                    disabledFrom: this.regularopeningtime,
                    to: this.extendedto || this.regularclosingtime
                },
                sizes: ['XS', 'S', 'M', 'L', 'XL'],
                locales: {
                  en: en,
                  es: es,
                  de: de
                },
                validationErrors: [],
            }
        } ,
        computed: {
            serverTimeMoment() {
                return moment.tz(this.serverTime, this.timezone);
            },
            today() {
                const d = this.serverTimeMoment.clone().startOf('day');
                return new Date(d.year(), d.month(), d.date());
            },
            scheduleConfig() {
                return {
                    timezone: this.timezone,
                    regularOpeningTime: this.regularopeningtime,
                    regularClosingTime: this.regularclosingtime,
                    scheduleSundayEnd: this.schedule_sunday_end,
                    extendedTo: this.extendedto,
                    tenant: this.tenant,
                    blockPastReservation: this.blockpastreservation,
                    preventNextMorningReservations: this.preventnextmorningreservations,
                    serverTimeMoment: this.serverTimeMoment,
                };
            },
            privacyRows () {
                const basic = this.lang.privacy_basic;

                if (!basic) {
                    return [];
                }

                return ['controller', 'purpose', 'legal_basis', 'recipients', 'rights']
                    .map((key) => ({ label: basic[`${key}_label`], value: basic[key] }))
                    .filter(({ label, value }) => label && value);
            },
            includedExtras () {
                return this.extras_list.filter(({ price }) => price['amount'] === "0")
            },
            paidExtras () {
                return this.extras_list.filter(({ price }) => price['amount'] !== "0");
            },
            pickupLocationOptions () {
                const isAfterHours = this.rent_start_hour > this.regularclosingtime;
                return Object.keys(this.locations).map((location) => ({
                    value: location,
                    label: this.locations[location],
                    disabled: isAfterHours
                        ? location != this.airport_location_id
                        : this.isLocationDisabled(location)
                }));
            },

            dropoffLocationOptions () {
                const isAfterHours = this.rent_end_hour > this.regularclosingtime;
                return Object.keys(this.locations).map((location) => ({
                    value: location,
                    label: this.locations[location],
                    disabled: isAfterHours
                        ? location != this.airport_location_id
                        : this.isLocationDisabled(location)
                }));
            },

            sizesOptions () {
                return this.sizes.map((size) => {
                    return {
                        value: size,
                        label: size
                    }
                })
            },
            isBike() {
                return this.vehicle_type === "bike"
            },
            // Todo lo que el servidor necesita para dar un precio. Cambiar cualquiera de
            // estas cosas deja las cifras de la pantalla viejas al instante, incluida la
            // espera de 800 ms del cupon.
            rateSignature() {
                return JSON.stringify([
                    this.vehicle_id,
                    this.rent_start,
                    this.rent_end,
                    this.rent_start_hour,
                    this.rent_end_hour,
                    this.voucher,
                    this.extras,
                ]);
            },
            ratesReady() {
                return this.calculatedRatesFor === this.rateSignature
                    && this.calculatedRates.total != null;
            },
            // Computada y no un dato que alguien actualice: cuando se recalculaba a mano
            // solo lo hacian los campos de texto y los desplegables, asi que elegir la
            // fecha en ultimo lugar dejaba el boton apagado con el formulario entero
            // relleno, y cambiar la de inicio despues lo dejaba encendido sin fecha de fin.
            disabled() {
                // Antes exigia un dominio de primer nivel de 2 a 4 letras y un juego de
                // caracteres corto, asi que un cliente con @algo.brussels o un apostrofo
                // en el nombre no podia encender el boton y nada le decia por que. El
                // servidor valida con filter_var, que los acepta.
                const testEmail = /^[^\s@]+@[^\s@]+\.[A-Z]{2,}$/i;
                const cond1 = this.renter_name === "" || this.renter_email === "" || this.renter_phone === ""
                    || this.rent_start === "" || this.rent_end === "" || !this.rent_start_hour
                    || !this.rent_end_hour || !this.tos || this.pickup_location === ""

                const cond2 = !testEmail.test(this.renter_email) || !Date.parse(this.rent_start) || !Date.parse(this.rent_end)
                const cond3 = this.isBike && this.size === ""
                const cond4 = this.dropoffEnabled && this.dropoff_location === ""
                const cond5 = !this.ratesReady

                return cond1 || cond2 || cond3 || cond4 || cond5;
            },
            dropoffEnabled() {
                return this.dropoff
            },
            isAfterHours() {
                return parseInt(this.rent_start_hour) > parseInt(this.regularclosingtime) ||
                    parseInt(this.rent_end_hour) > parseInt(this.regularclosingtime);
            },
        },
        methods:
        {
            /**
             * Lo que cuesta un extra, para la etiqueta que va junto a su control.
             *
             * La plantilla pedía `formatted_price`, un campo que nunca ha existido —lo que llega es
             * `price.formatted`—, así que la píldora del precio salía vacía y un extra de pago se
             * ofrecía sin decir su precio. Esto es lo que calculaba `getExtraLabel`, que estaba aquí
             * sin que nadie lo llamara, menos el nombre: el nombre ya está al lado.
             */
            extraPriceLabel(extra){
                let label = extra.price['formatted'];

                if(!extra.single_charge){
                    label += this.lang.eurosperday;
                }

                if(Number(extra.max_charge.amount) > 0){
                    label += ' · máx. ' + extra.max_charge['formatted'];
                }

                return label;
            },
            onExtraChanged({ value, selected }){
                if(selected){
                    this.$set(this.extras, value, 1);
                }else{
                    this.$delete(this.extras, value);
                }

                this.calculateRate();
            },

            /**
             * Las unidades que se ofrecen de un extra: desde ninguna hasta su máximo.
             */
            extraUnitOptions(extra){
                return Array.from({ length: Number(extra.max_units) + 1 }, (unused, units) => units);
            },

            onExtraUnitsChanged(id, units){
                const chosen = parseInt(units, 10);

                // Cero es quitarlo, no llevarlo a cero: lo que el servidor recibe es la lista de lo
                // contratado, y un extra a cero borraría el apunte de todas formas.
                if(chosen > 0){
                    this.$set(this.extras, id, chosen);
                }else{
                    this.$delete(this.extras, id);
                }

                this.calculateRate();
            },

            toServerMoment(date) {
                return moment.tz([date.getFullYear(), date.getMonth(), date.getDate()], this.timezone);
            },

            /**
             * Format a date for server submission, preserving the date value
             * without timezone conversion issues.
             * Takes a Date object and returns it formatted in server timezone
             * by constructing the moment directly in that timezone.
             */
            formatDateForServer(date) {
                // Extract year, month, day from the Date object (local values)
                const year = date.getFullYear();
                const month = date.getMonth() + 1; // getMonth() is 0-indexed
                const day = date.getDate();

                // Create a moment in the server timezone with these date components
                // This ensures the date value is preserved without timezone conversion
                return moment.tz(`${year}-${month}-${day}`, 'YYYY-M-D', this.timezone);
            },

            isDayDisabled(date){
                return svcIsDayBlocked(date, this.reservedDates, this.scheduleConfig) ||
                       svcIsHoliday(date, this.holidays, this.timezone) ||
                       isTooLateForToday(date, this.scheduleConfig);
            },

            getEndOfTheDay(date) {
                return getEndOfDay(date, this.scheduleConfig);
            },

            getEarliestAllowedEndDate(date){
                return getEarliestAllowedEndDate(date, this.minreservationdays, this.timezone);
            },

            getTimeConstraints(date) {
                return getTimeConstraintsForDate(date, this.reservedDates, this.scheduleConfig);
            },

            onTimePicker1Set(date){
                this.rent_start_hour = date;
                if(this.rent_start_hour > this.regularclosingtime){
                    this.pickup_location = this.airport_location_id;
                }
            },

            onTimePicker2Set(date){
                this.rent_end_hour = date;
                if(this.rent_end_hour > this.regularclosingtime){
                    this.dropoff_location = this.airport_location_id;
                }
                this.calculateRate();
            },

            onDatepicker1Set(date) {
                const { from, to } = this.getTimeConstraints(date);
                this.timepicker1.disabledFrom = from;
                this.timepicker1.to = to;
                this.rent_start_hour = from;

                let earliestAllowedEndDate = this.getEarliestAllowedEndDate(date);

                // Reset end date fields
                this.rent_end = '';
                this.rent_end_hour = this.regularopeningtime;

                // Update highlights and disabled states
                this.updateDatepickerStates(date, earliestAllowedEndDate);

                this.calculatedRates = {};
            },

            updateDatepickerStates(startDate, earliestAllowedEndDate) {
                this.datepicker1.highlighted.from = this.getDateOf(startDate);
                this.datepicker1.highlighted.to = '';

                this.datepicker2.isDisabled = false;
                this.datepicker2.from = this.regularopeningtime;
                this.datepicker2.opendate = this.getDateOf(startDate);
                this.datepicker2.highlighted.from = this.getDateOf(earliestAllowedEndDate);
                this.datepicker2.highlighted.to = '';

                this.$nextTick(() => {
                    this.datepicker2.disabled.to = this.getDateOf(earliestAllowedEndDate);
                    let from = this.getFirstDisabledDateAfterStart();
                    if (from) {
                        let hour = from.toDate().getHours();
                        if (hour < this.regularopeningtime) {
                            from.subtract(1, 'd');
                        }
                        this.datepicker2.disabled.from = this.getDateOf(from.toDate());
                    } else {
                        this.datepicker2.disabled.from = null;
                    }
                });
            },

            getFirstDisabledDateAfterStart(){
                return getFirstBlockerAfterStart(this.rent_start, this.rent_start_hour, this.reservedDates, this.timezone);
            },

            onDatepicker2Set(date){
                let nextBlocker = this.getFirstDisabledDateAfterStart();
                this.timepicker2.to = nextBlocker && nextBlocker.isValid() && nextBlocker.isSame(this.toServerMoment(date), 'day') ? parseInt(nextBlocker.hours()) : this.getEndOfTheDay(this.rent_end);

                // If the start and end dates are the same, set disabledFrom to rent_start_hour + 1
                if (
                    this.rent_start &&
                    this.rent_end &&
                    this.getDateOf(this.rent_start).getTime() === this.getDateOf(this.rent_end).getTime()
                ) {
                    this.timepicker2.disabledFrom = getSameDayEndHourMin(this.rent_start_hour);
                    if (parseInt(this.rent_end_hour) < this.timepicker2.disabledFrom) {
                        this.rent_end_hour = this.timepicker2.disabledFrom;
                    }
                } else {
                    this.timepicker2.disabledFrom = this.regularopeningtime;
                }

                this.datepicker1.highlighted.to = this.getDateOf(this.rent_end);
                this.datepicker2.highlighted.to = this.getDateOf(this.rent_end);
                this.calculateRate();
            },

            applyVoucher: function(value){
                this.updateField({ name: 'voucher', value: value })
                if (this.timeout)
                    clearTimeout(this.timeout);
                this.timeout = setTimeout(() => {
                    this.calculateRate();
                }, 800);
            },

            updateField({ name, value }) {
                this[name] = value
                //JairaCustomLogic for pickups
                if(this.tenant === 'jaira' && this.rent_start_hour === 9 && this.pickup_location == 6){
                    this.pickupText = this.lang.earliestPickupTime
                }else{
                    this.pickupText = ''
                }
            },

            async calculateRate() {
                if (!this.rent_start || !this.rent_end || !this.rent_start_hour || !this.rent_end_hour) {
                    return;
                }

                // La combinacion por la que se pregunta, para reconocer la respuesta al
                // volver: dos peticiones seguidas pueden llegar en el orden contrario y la
                // vieja pintaria el precio de unas fechas con otras en el formulario.
                const asked = this.rateSignature;
                this.rateError = false;

                try {
                    const queryParams = new URLSearchParams({
                        vehicle_id: parseInt(this.vehicle_id),
                        rent_start: this.formatDateForServer(this.rent_start).format('DD/MM/YYYY'),
                        rent_start_hour: `${parseInt(this.rent_start_hour) === 24 ? 23 : parseInt(this.rent_start_hour)}:00`,
                        rent_end: this.formatDateForServer(this.rent_end).format('DD/MM/YYYY'),
                        rent_end_hour: `${parseInt(this.rent_end_hour) === 24 ? 23 : parseInt(this.rent_end_hour)}:00`,
                        voucher_code: this.voucher,
                        ...this.getExtrasParams()
                    }).toString();

                    const response = await axios.get(`/calculate?${queryParams}`);

                    if (asked !== this.rateSignature) {
                        return;
                    }

                    this.calculatedRates = response.data;
                    this.calculatedRatesFor = asked;
                    this.voucher_status = this.calculatedRates.voucher_status;
                } catch (error) {
                    console.error('Failed to calculate rates:', error);

                    // Sin cifras el boton no se enciende, asi que el fallo tiene que verse:
                    // un boton apagado sobre un formulario relleno y sin explicacion es
                    // justo lo que no se podia contar por telefono.
                    if (asked === this.rateSignature) {
                        this.rateError = true;
                    }
                }
            },

            getExtrasParams() {
                return Object.entries(this.extras).reduce((params, [id, units]) => {
                    params[`extras[${id}]`] = units;
                    return params;
                }, {});
            },

            submit(){
                let payload = {
                    renter_name: this.renter_name,
                    renter_email: this.renter_email,
                    renter_phone: this.renter_phone,
                    voucher_code: this.voucher,
                    tos: Boolean(this.tos),
                    vehicle_id: parseInt(this.vehicle_id),
                    vehicle_size: this.size,
                    extras : [],
                    rent_start: this.formatDateForServer(this.rent_start).format('DD/MM/YYYY'),
                    rent_start_hour: parseInt(this.rent_start_hour) + ':00',
                    rent_end: this.formatDateForServer(this.rent_end).format('DD/MM/YYYY'),
                    rent_end_hour: parseInt(this.rent_end_hour) + ':00',
                    pickup_location: this.pickup_location,
                    dropoff_location: this.dropoff_location
                }
                Object.entries(this.extras).forEach(([id, units]) => {
                    payload.extras[id] = units;
                });

                // Clear previous validation errors
                this.validationErrors = [];

                axios.post('/' + bike.locale + '/reservation', payload)
                    .then(response => window.location.href = response.data)
                    .catch(error => {
                        if (error.response && error.response.status === 422) {
                            // Validation failed - display errors
                            this.validationErrors = error.response.data.errors || [];
                            // Scroll to top to show errors
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        } else {
                            // Other errors - show generic message
                            alert('An error occurred. Please try again.');
                        }
                    });
            },

            getLocale(){
              return getDatepickerLocale(this.bike.locale);
            },

            getDateOf(date){
              return svcGetDateOf(date);
            },
            isLocationDisabled(location){
                const options = this.pickup_options || ['todos'];
                if (options.includes('todos') || options.includes(location)) return false;
                return true;
            }
        },
        watch: {
            rent_start_hour(newHour) {
                // --- update disabledFrom for timepicker2 if dates are the same ---
                if (
                    this.rent_start &&
                    this.rent_end &&
                    this.getDateOf(this.rent_start).getTime() === this.getDateOf(this.rent_end).getTime()
                ) {
                    this.timepicker2.disabledFrom = getSameDayEndHourMin(newHour);
                    if (parseInt(this.rent_end_hour) < this.timepicker2.disabledFrom) {
                        this.rent_end_hour = this.timepicker2.disabledFrom;
                    }
                } else {
                    this.timepicker2.disabledFrom = this.regularopeningtime;
                }

                this.calculateRate();
            }
        },
        mounted:function () {
          // Initialize datepicker boundaries with server time
          this.datepicker1.disabled.to = this.today;
          this.datepicker2.disabled.to = this.today;
          this.datepicker2.opendate = this.today;

          axios.get("/availability/calendar/" +  parseInt(this.vehicle_id))
              .then(response => {
                  this.reservedDates = response.data.blockedDays;
                  this.holidays = response.data.holidays;

                  this.$nextTick(function () {
                      let urlParams = new URLSearchParams(window.location.search);
                      if (urlParams.get('start')) {
                          const start = moment.tz(urlParams.get('start'),'YYYY-MM-DD H:00:00', this.timezone);
                          if (start.isValid() && !this.isDayDisabled(start.toDate())) {
                              this.rent_start = start.toDate();
                              const { from, to } = this.getTimeConstraints(this.rent_start);
                              this.timepicker1.disabledFrom = from;
                              this.timepicker1.to = to;

                              let requestedHour = parseInt(start.format('H'));
                              this.rent_start_hour = requestedHour < from ? from : (requestedHour > to ? to : requestedHour);

                              let earliestAllowedEndDate = this.getEarliestAllowedEndDate(this.rent_start);
                              this.updateDatepickerStates(this.rent_start, earliestAllowedEndDate);
                          }
                      }
                      if (urlParams.get('end')) {
                          const end = moment.tz(urlParams.get('end'),'YYYY-MM-DD H:00:00', this.timezone);
                          if (end.isValid() && !this.isDayDisabled(end.toDate())) {
                              // Ensure end is after start
                              if (!this.rent_start || end.isSameOrAfter(this.toServerMoment(this.rent_start), 'day')) {
                                  this.rent_end = end.toDate();

                                  let nextBlocker = this.getFirstDisabledDateAfterStart();
                                  this.timepicker2.to = nextBlocker && nextBlocker.isValid() && nextBlocker.isSame(this.toServerMoment(this.rent_end), 'day') ? parseInt(nextBlocker.hours()) : this.getEndOfTheDay(this.rent_end);

                                  // If the start and end dates are the same, set disabledFrom to rent_start_hour + 1
                                  if (
                                      this.rent_start &&
                                      this.rent_end &&
                                      this.getDateOf(this.rent_start).getTime() === this.getDateOf(this.rent_end).getTime()
                                  ) {
                                      this.timepicker2.disabledFrom = getSameDayEndHourMin(this.rent_start_hour);
                                  } else {
                                      this.timepicker2.disabledFrom = this.regularopeningtime;
                                  }

                                  let requestedEndHour = parseInt(end.format('H'));
                                  this.rent_end_hour = requestedEndHour < this.timepicker2.disabledFrom ? this.timepicker2.disabledFrom : (requestedEndHour > this.timepicker2.to ? this.timepicker2.to : requestedEndHour);

                                  this.datepicker1.highlighted.to = this.getDateOf(this.rent_end);
                                  this.datepicker2.highlighted.to = this.getDateOf(this.rent_end);
                              }
                          }
                      }
                      this.calculateRate();
                  });
              });
        }
    }
</script>

<style>
.checkout-page__form-date-time-fields .datepicker-input, .checkout-page__form-date-time-fields .select-form {
  background: transparent;
  border: none;
  box-shadow: none;
}
.checkout-page__form-date-time-fields .datepicker-input {
  width: 100%;
}
.checkout-page__form-date-time-fields .datepicker-input::placeholder {
  color: black;
}
.checkout-page__form-date-time-fields .select-form {
  font-size: 0.875rem;
  line-height: 1.2;
  flex: 1;
}
.checkout-page__form-date-time-fields .datepicker-input:focus, .checkout-page__form-date-time-fields .select-form:focus {
  outline: 0;
}
</style>
